import { AnimatePresence, m } from '../../lib/motion.js';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/format.js';
import { useReducedMotion } from '../../hooks/useReducedMotion.js';

/**
 * The mega menu, one panel per world.
 *
 * Rendered inside the header rather than as an overlay so it pushes nothing and
 * shifts nothing, and it carries the world's accent through data-world — the
 * same mechanism the rest of the shell uses.
 */
export const MegaMenu = ({ world, categories, onMouseEnter, onMouseLeave, onNavigate }) => {
  const reduced = useReducedMotion();

  const roots = (categories ?? []).filter((category) => category.world === world);

  return (
    <AnimatePresence>
      {world && roots.length > 0 && (
        <m.div
          data-world={world}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
          animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reduced ? { opacity: 0 } : { opacity: 0, y: -8 }}
          transition={{ duration: reduced ? 0.12 : 0.2, ease: [0.25, 1, 0.5, 1] }}
          className="absolute inset-x-0 top-full hidden border-y border-mist bg-surface lg:block"
        >
          <div className="mx-auto grid max-w-(--container-content) grid-cols-[repeat(4,1fr)] gap-8 px-6 py-9">
            {roots.map((root) => (
              <div key={root._id}>
                <Link
                  to={`/category/${root.slug}`}
                  onClick={onNavigate}
                  className="font-display text-sm tracking-[-0.01em] text-ink transition-colors hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  {root.name}
                </Link>

                {root.children?.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {root.children.map((child) => (
                      <li key={child._id}>
                        <Link
                          to={`/category/${child.slug}`}
                          onClick={onNavigate}
                          className="text-xs text-muted transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                        >
                          {child.name}
                          {child.productCount > 0 && (
                            <span className="ml-1.5 font-mono text-2xs text-mist" data-numeric>
                              {child.productCount}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-mist bg-paper">
            <div className="mx-auto flex max-w-(--container-content) items-center justify-between gap-6 px-6 py-3">
              <p className="text-2xs text-muted">
                {world === 'fragrance'
                  ? 'Not sure where to start? A discovery set is six vials and the cost comes off your first bottle.'
                  : 'Every specification on every page. Compare before you commit.'}
              </p>
              <Link
                to={`/shop?world=${world}`}
                onClick={onNavigate}
                className={cn(
                  'shrink-0 font-mono text-2xs tracking-[0.1em] text-accent uppercase',
                  'underline decoration-transparent underline-offset-4 transition-colors hover:decoration-current',
                )}
              >
                See everything →
              </Link>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default MegaMenu;
